import './ViewPost.sass';

import React, { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AnimatedEmoji, Header, List, ListItem, Placeholder, ScrollView } from 'uikit';
import {
  usePost,
  usePostMeta,
  usePosts,
  useScheduled,
  useTranslator,
  useUnitInfo,
  useUserInfo,
} from 'hooks';
import {
  usePostIsDeletedSelector,
  usePostIsUnavailableSelector,
  postsActions,
  postsSelector,
} from 'store/posts';
import { Loader, MetaTags, Post, UnitName } from '$shared/components';
import { CommentForm } from '$shared/components/CommentForm/CommentForm';
import { Comment } from '$shared/components';
import { ViewPostProps } from '$core/ViewPost/ViewPost.interface';
import { goBack, showModal } from '$navigation/helper';

export const ViewPost: FC<ViewPostProps> = ({ postId }) => {
  const dispatch = useDispatch();
  const post = usePost(postId);
  const { isPostLoading, isCommentsLoadMore, commentsNextFrom } =
    useSelector(postsSelector);
  const comments = usePosts(`comments_${post?.id}`);
  const t = useTranslator();
  const { title, description } = usePostMeta(post);
  const parentOwner = useUnitInfo(post ? post.parentOwnerId || 0 : 0);
  const user = useUserInfo(post?.publisherId);
  const isUnavailable = usePostIsUnavailableSelector(postId);
  const isDeleted = usePostIsDeletedSelector(postId);

  const handleLoadMore = useCallback(() => {
    dispatch(postsActions.loadMoreComments(postId));
  }, [postId, dispatch]);

  useEffect(() => {
    dispatch(postsActions.loadPost(postId));
  }, [postId]);

  const isScheduled = useScheduled(post?.createdAt);

  const handleOpenParent = useCallback(() => {
    showModal(`post${post.parentId}`);
    return false;
  }, [post]);

  function deletedRender() {
    return (
      <>
        <MetaTags title={title} description={description} />
        <Header showBackButton backButtonOnClick={() => goBack()}>
          {t('view_post_title')}
        </Header>
        <Placeholder
          icon={<AnimatedEmoji emoji={'❌'} />}
          iconSize={120}
          title={<span>{t('view_post_deleted_title')}</span>}
        >
          <span>{t('view_post_deleted_text')}</span>
        </Placeholder>
      </>
    );
  }

  function bannedRender() {
    return (
      <>
        <MetaTags title={title} description={description} />
        <Header showBackButton backButtonOnClick={() => goBack()}>
          {t('view_post_title')}
        </Header>
        <Placeholder
          icon={<AnimatedEmoji emoji={'🤔'} />}
          iconSize={120}
          title={<span>{t('view_post_banned_title')}</span>}
        >
          <span>{t('view_post_banned_text')}</span>
        </Placeholder>
      </>
    );
  }

  function countryBlockedRender() {
    return (
      <>
        <MetaTags title={title} description={description} />
        <Header showBackButton backButtonOnClick={() => goBack()}>
          {t('view_post_title')}
        </Header>
        <Placeholder
          icon={<AnimatedEmoji emoji={'🤔'} />}
          iconSize={120}
          title={<span>{t('view_post_unavailable_title')}</span>}
        >
          <span>{t('view_post_unavailable_text')}</span>
        </Placeholder>
      </>
    );
  }

  function renderContent() {
    if ((isPostLoading[postId] && !post) || !post) {
      return (
        <div
          style={{
            padding: '100px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loader />
        </div>
      );
    }

    return (
      <>
        <ScrollView
          canLoadMore={
            !isPostLoading[postId] &&
            !!post &&
            !isCommentsLoadMore[postId] &&
            !!commentsNextFrom[postId]
          }
          onLoadMore={handleLoadMore}
          isLoadingMore={isCommentsLoadMore[postId] || isPostLoading[postId]}
        >
          <div className="ViewPost">
            {post && !!post.parentOwnerId && (
              <div className="ViewPost__parent">
                <List>
                  <ListItem onClick={handleOpenParent} chevron>
                    {t('view_post_parent_button')}{' '}
                    <UnitName inclineCase="genitive" unit={parentOwner} />
                  </ListItem>
                </List>
              </div>
            )}
            <Post postId={postId} isFullPage />
            {!isScheduled && (
              <>
                <div className="ViewPost__comments">
                  {post.comments > 0
                    ? t('replies_count', {
                        count: post.comments,
                      })
                    : t('view_post_no_comments')}
                </div>
                <div>
                  {comments.map((commentId) => (
                    <Comment key={commentId} rootPostId={postId} commentId={commentId} />
                  ))}
                </div>
              </>
            )}
          </div>
        </ScrollView>
        {!isScheduled && <CommentForm parentId={post.id} />}
      </>
    );
  }

  if (isDeleted) {
    return deletedRender();
  }

  if (user?.isBanned) {
    return bannedRender();
  }

  if (isUnavailable) {
    return countryBlockedRender();
  }

  console.log('is available and notbanned');
  return (
    <>
      <MetaTags title={title} description={description} />
      <Header showBackButton backButtonOnClick={() => goBack()}>
        {t('view_post_title')}
      </Header>
      {renderContent()}
    </>
  );
};
